import { Component } from '@theme/component';

/**
 * Direction keywords for filtering stitches
 */
const STITCH_DIRECTIONS = ['diagonal', 'straight', 'cross', 'specialty'];

/**
 * Stitch Library — interactive reference grid.
 *
 * Drives tabs, filters, search, "load more" pagination, the in-page detail
 * block, and a YouTube modal — all backed by `stitch` metaobjects rendered
 * server-side by `sections/stitch-library.liquid`.
 *
 * @typedef {object} StitchLibraryRefs
 * @property {HTMLElement} grid - Card grid wrapper.
 * @property {HTMLElement[]} cards - Stitch card articles.
 * @property {HTMLElement} [videosGrid] - Videos-tab grid wrapper.
 * @property {HTMLButtonElement[]} tabs - Tab buttons.
 * @property {HTMLSelectElement[]} filters - Filter selects.
 * @property {HTMLInputElement} search - Search input.
 * @property {HTMLButtonElement} [loadMore] - "Load more" button.
 * @property {HTMLElement} [count] - Visible count number.
 * @property {HTMLElement} [total] - Total count number.
 * @property {HTMLElement} [empty] - Empty-state element.
 * @property {HTMLElement} detail - In-page detail block.
 * @property {HTMLButtonElement} detailBack - Back-to-library button.
 * @property {HTMLElement} videoModal - Video modal wrapper.
 * @property {HTMLButtonElement} videoClose - Modal close button.
 * @property {HTMLIFrameElement} videoFrame - Modal iframe.
 *
 * @extends {Component<StitchLibraryRefs>}
 */
class StitchLibrary extends Component {
  requiredRefs = ['grid'];

  /** @type {number} */
  #pageSize = 8;

  /** @type {number} */
  #visibleLimit = 8;

  /** @type {string} */
  #activeTab = 'all';

  /** @type {number | null} */
  #searchTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#pageSize = parseInt(this.getAttribute('data-page-size') || '8', 10) || 8;
    this.#visibleLimit = this.#pageSize;

    document.addEventListener('keydown', this.#handleKeydown);

    // If the page was opened with a #stitch-handle hash, open that card's detail.
    // Otherwise default to showing the first card's detail so the detail block
    // never renders as an empty placeholder.
    const initialHash = (window.location.hash || '').replace(/^#/, '');
    if (initialHash && initialHash !== 'stitch-library') {
      const target = this.#cards.find((c) => c.dataset.handle === initialHash.replace(/^stitch-/, ''));
      if (target) {
        // Defer so refs are fully resolved before reading the payload
        requestAnimationFrame(() => this.#showDetailFor(target));
      }
    } else {
      const firstCard = this.#cards[0];
      if (firstCard) {
        requestAnimationFrame(() => this.#showDetailFor(firstCard, { scroll: false, updateHash: false }));
      }
    }

    this.#applyFilters();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleKeydown);
    if (this.#searchTimer) {
      clearTimeout(this.#searchTimer);
      this.#searchTimer = null;
    }
  }

  /**
   * Returns the list of card elements in a stable order.
   * @returns {HTMLElement[]}
   */
  get #cards() {
    const cards = this.refs.cards;
    if (!cards) return [];
    return Array.isArray(cards) ? cards : [cards];
  }

  /**
   * Returns the list of filter <select> elements.
   * @returns {HTMLSelectElement[]}
   */
  get #filterSelects() {
    const filters = this.refs.filters;
    if (!filters) return [];
    return /** @type {HTMLSelectElement[]} */ (Array.isArray(filters) ? filters : [filters]);
  }

  /**
   * Returns the list of tab buttons.
   * @returns {HTMLButtonElement[]}
   */
  get #tabButtons() {
    const tabs = this.refs.tabs;
    if (!tabs) return [];
    return /** @type {HTMLButtonElement[]} */ (Array.isArray(tabs) ? tabs : [tabs]);
  }

  /**
   * Reads the current filter state from the DOM.
   * @returns {{ difficulty: string, use: string, direction: string, q: string }}
   */
  #getFilterValues() {
    const values = { difficulty: '', use: '', direction: '' };
    for (const sel of this.#filterSelects) {
      const key = sel.getAttribute('data-stitch-filter');
      if (key && key in values) {
        values[/** @type {keyof typeof values} */ (key)] = (sel.value || '').toLowerCase();
      }
    }
    const searchInput = this.refs.search;
    const q = searchInput instanceof HTMLInputElement ? searchInput.value.trim().toLowerCase() : '';
    return { ...values, q };
  }

  /**
   * Whether a card matches the currently selected tab.
   * @param {HTMLElement} card
   * @param {string} tab
   * @returns {boolean}
   */
  #tabMatches(card, tab) {
    if (tab === 'all' || tab === 'diagrams') return true;
    if (tab === 'videos') return card.dataset.hasVideo === 'true';
    if (tab === 'beginner') return (card.dataset.difficulty || '').toLowerCase() === 'beginner';
    if (tab === 'decorative') return (card.dataset.category || '').toLowerCase().indexOf('decorative') !== -1;
    return true;
  }

  /**
   * Applies tab + filter + search state to the card grid and updates counts.
   */
  #applyFilters() {
    const { difficulty, use, direction, q } = this.#getFilterValues();
    let visibleCount = 0;
    let matchTotal = 0;

    for (const card of this.#cards) {
      const name = (card.dataset.name || '').toLowerCase();
      const cardDifficulty = (card.dataset.difficulty || '').toLowerCase();
      const cardUse = (card.dataset.use || '').toLowerCase();
      const cardDescription = (card.dataset.description || '').toLowerCase();
      const cardTips = (card.dataset.tips || '').toLowerCase();

      // For direction filter: check if description, tips/notes, or name contains the selected direction
      let directionMatches = true;
      if (direction) {
        directionMatches = 
          name.indexOf(direction) !== -1 || 
          cardDescription.indexOf(direction) !== -1 || 
          cardTips.indexOf(direction) !== -1;
      }

      const matches =
        this.#tabMatches(card, this.#activeTab) &&
        (!difficulty || cardDifficulty === difficulty) &&
        (!use || cardUse.indexOf(use) !== -1) &&
        directionMatches &&
        (!q || name.indexOf(q) !== -1);

      if (!matches) {
        card.hidden = true;
        continue;
      }

      matchTotal++;
      if (visibleCount < this.#visibleLimit) {
        card.hidden = false;
        visibleCount++;
      } else {
        card.hidden = true;
      }
    }

    const { count, total, empty, loadMore } = this.refs;
    if (count instanceof HTMLElement) count.textContent = String(visibleCount);
    if (total instanceof HTMLElement) total.textContent = String(matchTotal);
    if (empty instanceof HTMLElement) empty.hidden = matchTotal > 0;
    if (loadMore instanceof HTMLElement) loadMore.hidden = visibleCount >= matchTotal;
  }

  /**
   * Handles selecting a tab.
   * @param {MouseEvent} event
   */
  selectTab = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    for (const tab of this.#tabButtons) {
      tab.setAttribute('aria-selected', tab === button ? 'true' : 'false');
    }

    this.#activeTab = button.getAttribute('data-stitch-tab') || 'all';
    this.#visibleLimit = this.#pageSize;
    this.#updateVideosMode();
    this.#applyFilters();
  };

  /**
   * Swaps the regular card grid for the simple video-embed grid when the
   * Videos tab is selected. The CSS hides filters / counts / pagination /
   * empty-state via the `data-videos-mode` attribute on the host element.
   */
  #updateVideosMode() {
    const inVideosMode = this.#activeTab === 'videos';
    this.toggleAttribute('data-videos-mode', inVideosMode);

    const grid = this.refs.grid;
    const videosGrid = this.refs.videosGrid;
    if (grid instanceof HTMLElement) grid.hidden = inVideosMode;
    if (videosGrid instanceof HTMLElement) videosGrid.hidden = !inVideosMode;
  }

  /**
   * Handles filter <select> changes.
   */
  changeFilter = () => {
    this.#visibleLimit = this.#pageSize;
    this.#applyFilters();
  };

  /**
   * Handles the search input (debounced).
   */
  searchInput = () => {
    if (this.#searchTimer) clearTimeout(this.#searchTimer);
    this.#searchTimer = window.setTimeout(() => {
      this.#searchTimer = null;
      this.#visibleLimit = this.#pageSize;
      this.#applyFilters();
    }, 150);
  };

  /**
   * Reveals the next chunk of cards.
   */
  loadMore = () => {
    this.#visibleLimit += this.#pageSize;
    this.#applyFilters();

    const firstNewCard = this.#cards.find((c) => !c.hidden && !c.dataset.seen);
    for (const c of this.#cards) {
      if (!c.hidden) c.dataset.seen = '1';
    }
    if (firstNewCard) {
      const focusable = firstNewCard.querySelector('button, a');
      if (focusable instanceof HTMLElement) focusable.focus({ preventScroll: true });
    }
  };

  /**
   * Opens the detail block populated from the clicked card's payload.
   * @param {MouseEvent} event
   */
  viewStitch = (event) => {
    const trigger = event.target;
    if (!(trigger instanceof HTMLElement)) return;
    const card = trigger.closest('[data-stitch-card]');
    if (card instanceof HTMLElement) this.#showDetailFor(card);
  };

  /**
   * Opens the YouTube video modal for the clicked card.
   * @param {MouseEvent} event
   */
  openVideo = (event) => {
    const trigger = event.target;
    if (!(trigger instanceof HTMLElement)) return;
    const url = trigger.getAttribute('data-video-url');
    this.#openVideoModal(url);
  };

  /**
   * Hides the detail block.
   */
  closeDetail = () => {
    const { detail } = this.refs;
    if (detail instanceof HTMLElement) {
      detail.hidden = true;
    }
    if (history.replaceState) {
      history.replaceState(null, '', '#stitch-library');
    }
  };

  /**
   * Closes the YouTube modal and stops playback.
   */
  closeVideo = () => {
    const { videoModal, videoFrame } = this.refs;
    if (videoFrame instanceof HTMLIFrameElement) videoFrame.src = '';
    if (videoModal instanceof HTMLElement) {
      videoModal.removeAttribute('open');
      videoModal.hidden = true;
    }
  };

  /**
   * Closes the modal when the user clicks the backdrop. The framework
   * routes this handler only when the click target's closest `on:click`
   * ancestor is the modal element itself — the close button and other
   * interactive children carry their own `on:click`, so they don't reach
   * this method.
   */
  modalBackdropClick = () => {
    this.closeVideo();
  };

  /**
   * Renders the detail block from a card's embedded JSON payload.
   * @param {HTMLElement} card
   * @param {{ scroll?: boolean, updateHash?: boolean }} [options]
   */
  #showDetailFor(card, options = {}) {
    const { scroll = true, updateHash = true } = options;
    const detail = this.refs.detail;
    if (!(detail instanceof HTMLElement)) return;

    const payloadEl = card.querySelector('[data-stitch-payload]');
    if (!payloadEl) return;

    let payload;
    try {
      payload = JSON.parse(payloadEl.textContent || '{}');
    } catch (_) {
      return;
    }

    for (const el of detail.querySelectorAll('[data-detail-field]')) {
      if (!(el instanceof HTMLElement)) continue;
      const field = el.getAttribute('data-detail-field');
      if (!field) continue;
      const value = payload[field];

      if (field === 'image') {
        const img = el.querySelector('img');
        if (img instanceof HTMLImageElement && value) {
          img.src = value;
          img.alt = (payload.name || 'Stitch') + ' diagram';
        }
        el.hidden = !value;
      } else if (field === 'difficulty') {
        el.textContent = value || '';
        el.className = 'stitch-card__badge';
        if (value) el.classList.add('stitch-card__badge--' + String(value).toLowerCase());
        el.hidden = !value;
      } else if (field === 'related') {
        el.innerHTML = '';
        const related = Array.isArray(payload.related) ? payload.related : [];
        for (const r of related) {
          const link = document.createElement('a');
          link.href = r.url || '#stitch-library';
          link.innerHTML =
            '<span class="stitch-library__related-img">' +
            (r.image ? '<img src="' + r.image + '" alt="' + (r.name || '') + '">' : '') +
            '</span><span>' + (r.name || '') + '</span>';
          el.appendChild(link);
        }
        el.hidden = related.length === 0;
      } else if (field === 'video_url') {
        if (value && value.trim() !== '') {
          el.setAttribute('data-video-url', value);
          el.hidden = false;
        } else {
          el.removeAttribute('data-video-url');
          el.hidden = true;
        }
      } else if (field === 'direction') {
        // For direction field: show value if description, tips/notes, or name contains a direction keyword
        const name = (payload.name || '').toLowerCase();
        const description = (payload.description || '').toLowerCase();
        const tips = (payload.tips || '').toLowerCase();
        let matchedDirection = '';
        
        // Check if any of the fields contain a direction keyword
        for (const dir of STITCH_DIRECTIONS) {
          if (name.indexOf(dir) !== -1 || description.indexOf(dir) !== -1 || tips.indexOf(dir) !== -1) {
            matchedDirection = dir.charAt(0).toUpperCase() + dir.slice(1);
            break;
          }
        }
        
        el.textContent = matchedDirection;
        const wrap = el.closest('[data-detail-row]');
        if (wrap instanceof HTMLElement) wrap.hidden = !matchedDirection;
      } else {
        el.textContent = value || '';
        const wrap = el.closest('[data-detail-row]');
        if (wrap instanceof HTMLElement) wrap.hidden = !value;
      }
    }

    detail.hidden = false;
    if (updateHash && history.replaceState && payload.handle) {
      history.replaceState(null, '', '#stitch-' + payload.handle);
    }
    if (scroll) {
      detail.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * @param {string | null} url - YouTube URL (watch / youtu.be / embed).
   */
  #openVideoModal(url) {
    if (!url) return;
    const { videoModal, videoFrame } = this.refs;
    if (!(videoModal instanceof HTMLElement) || !(videoFrame instanceof HTMLIFrameElement)) return;

    const embed = toYouTubeEmbed(url);
    if (!embed) return;

    videoFrame.src = embed;
    videoModal.hidden = false;
    videoModal.setAttribute('open', '');
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      const modal = this.refs.videoModal;
      if (modal instanceof HTMLElement && modal.hasAttribute('open')) {
        this.closeVideo();
      }
    }
  };
}

/**
 * Convert a YouTube URL (`watch?v=…`, `youtu.be/…`, `embed/…`) into an embed URL.
 *
 * @param {string} url
 * @returns {string | null}
 */
function toYouTubeEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.indexOf('youtu.be') !== -1) {
      return 'https://www.youtube.com/embed' + u.pathname;
    }
    if (u.hostname.indexOf('youtube.com') !== -1) {
      const id = u.searchParams.get('v');
      if (id) return 'https://www.youtube.com/embed/' + id;
      if (u.pathname.indexOf('/embed/') === 0) return url;
    }
    return url;
  } catch (_) {
    return null;
  }
}

if (!customElements.get('stitch-library-component')) {
  customElements.define('stitch-library-component', StitchLibrary);
}
